import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import { ShieldCheck, Clock, Activity, AlertTriangle, Monitor, Info, Search, Building } from 'lucide-react';

const Auditoria = () => {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [filtro, setFiltro] = useState(''); // Estado para el buscador

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return navigate('/login');

    try {
      const payloadBase64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
      const decodedPayload = JSON.parse(window.atob(payloadBase64));
      const userRole = decodedPayload["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"] || decodedPayload.role;
      
      if (userRole !== 'SuperAdmin') {
        alert("Acceso denegado: Área exclusiva de Seguridad EMD.");
        navigate('/dashboard'); 
        return; 
      }
    } catch (error) {
      navigate('/login');
      return;
    }

    cargarAuditoria(token);
  }, [navigate]);

  async function cargarAuditoria(token) {
    setCargando(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/Auditorias`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setLogs(await res.json());
    } catch (error) {
      console.error("Error de conexión", error);
    } finally {
      setCargando(false);
    }
  }

  const getEstiloAccion = (accion) => {
    const acc = accion.toLowerCase();
    if (acc.includes('crear') || acc.includes('insert') || acc.includes('inicio')) return { color: 'text-emerald-600', bg: 'bg-emerald-50', icon: <Activity size={16} /> };
    if (acc.includes('eliminar') || acc.includes('delete') || acc.includes('fallido') || acc.includes('incorrecta')) return { color: 'text-red-600', bg: 'bg-red-50', icon: <AlertTriangle size={16} /> };
    return { color: 'text-slate-600', bg: 'bg-slate-100', icon: <Info size={16} /> };
  };

  // Motor de Búsqueda: Filtra en tiempo real por usuario, colegio o acción
  const logsFiltrados = logs.filter(log => 
    log.usuarioCorreo.toLowerCase().includes(filtro.toLowerCase()) ||
    log.colegioNombre.toLowerCase().includes(filtro.toLowerCase()) ||
    log.accion.toLowerCase().includes(filtro.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-100 flex">
      <Sidebar />
      
      <main className="flex-1 p-6 overflow-y-auto">
        <header className="mb-6">
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <ShieldCheck className="text-slate-800" size={28} />
            Registro de Auditoría y Seguridad
          </h1>
          <p className="text-slate-500 mt-1">Monitoreo centralizado Multi-Tenant (B2B).</p>
        </header>

        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          
          {/* BARRA DE BÚSQUEDA Y CONTROLES */}
          <div className="p-4 border-b bg-slate-50 flex justify-between items-center gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <input 
                type="text" 
                placeholder="Buscar por correo, colegio o acción..." 
                className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm"
                value={filtro}
                onChange={(e) => setFiltro(e.target.value)}
              />
            </div>
            <button onClick={() => cargarAuditoria(localStorage.getItem('token'))} className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 font-bold bg-blue-50 px-4 py-2 rounded-lg">
              <Clock size={16} /> Actualizar
            </button>
          </div>

          <div className="p-0">
            {cargando ? (
              <div className="text-center py-10 font-bold text-slate-400">Cargando registros de seguridad...</div>
            ) : logsFiltrados.length === 0 ? (
              <div className="text-center py-10 text-slate-500">No se encontraron registros para tu búsqueda.</div>
            ) : (
              <table className="w-full text-left text-sm">
                <thead className="bg-white border-b text-slate-500 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="p-4 font-bold">Fecha</th>
                    <th className="p-4 font-bold">Usuario y Colegio</th>
                    <th className="p-4 font-bold">Acción</th>
                    <th className="p-4 font-bold">Detalles</th>
                    <th className="p-4 font-bold text-center">IP</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {logsFiltrados.map((log) => {
                    const estilo = getEstiloAccion(log.accion);
                    const fecha = new Date(log.fechaHora).toLocaleString('es-PE', { dateStyle: 'medium', timeStyle: 'short' });

                    return (
                      <tr key={log.id} className="hover:bg-slate-50 transition-colors">
                        <td className="p-4 text-slate-500 font-medium whitespace-nowrap">{fecha}</td>
                        <td className="p-4">
                          <div className="font-bold text-slate-800">{log.usuarioCorreo}</div>
                          <div className="flex items-center gap-1 text-xs text-blue-600 mt-1 font-medium">
                            <Building size={12} /> {log.colegioNombre}
                          </div>
                        </td>
                        <td className="p-4">
                          <span className={`flex items-center gap-1.5 w-fit px-2.5 py-1 rounded font-bold text-xs uppercase tracking-wider ${estilo.bg} ${estilo.color}`}>
                            {estilo.icon}
                            {log.accion}
                          </span>
                        </td>
                        <td className="p-4 text-slate-600 max-w-sm" title={log.detalles}>
                          {log.detalles}
                        </td>
                        <td className="p-4 text-center">
                          <div className="flex items-center justify-center gap-1.5 text-xs text-slate-400 font-mono bg-slate-50 py-1 px-2 rounded border">
                            <Monitor size={12} />
                            {log.ipAddress}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default Auditoria;